import React, { useMemo } from "react";
import { Table, type TableProps } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table/interface";
import { useTranslation } from "react-i18next";
import { DownArrowIcon } from "@/components/icons";
import { buildOrderChannelRows } from "@/utils/orderV2";
import { useIsMobileMd } from "@/hooks/useIsMobileMd";

type Props = Omit<TableProps<any>, "expandable" | "rowKey" | "rowClassName"> & {
  buildChannelRows?: (order: any) => any[];
  expandedOrderKeys: React.Key[];
  onExpandedOrderKeysChange: (keys: React.Key[]) => void;
};

// Tree rows share the parent's colgroup, fixed cells and horizontal scroller.
export default function CreatorChannelTable({
  dataSource,
  buildChannelRows = buildOrderChannelRows,
  columns,
  rowSelection,
  expandedOrderKeys,
  onExpandedOrderKeysChange,
  ...tableProps
}: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobileMd();
  const rows = useMemo(
    () =>
      dataSource?.map((order) => {
        const channels = buildChannelRows(order);
        return {
          ...order,
          __channelRowKey: order.id,
          __channelChildren: channels.length > 1
            ? channels.map((channel) => ({
                ...channel,
                __channelRowKey: JSON.stringify([
                  order.id,
                  "channel",
                  channel.channelKey,
                ]),
                __channelRowType: "channel",
              }))
            : undefined,
        };
      }),
    [dataSource, buildChannelRows],
  );
  const hasExpandableRows = rows?.some((row) => row.__channelChildren?.length);

  const sharedColumns = useMemo<ColumnsType<any>>(
    () => [
      ...(rowSelection
        ? [Table.SELECTION_COLUMN]
        : hasExpandableRows
          ? [{
              key: "__channelExpand",
              width: 32,
              fixed: isMobile ? undefined : "left" as const,
              className: "order-channel-expand-cell",
              render: () => null,
            }]
          : []),
      ...((columns ?? []) as ColumnType<any>[]).map((column) => ({
        ...column,
        fixed: isMobile ? undefined : column.fixed,
        render: (value: any, record: any, index: number) => {
          if (!record.__channelRowType) {
            return column.render ? column.render(value, record, index) : value;
          }
          if (column.dataIndex === "isStar" || column.title === "Actions") {
            return null;
          }
          return column.render ? column.render(value, record, index) : value;
        },
      })),
    ],
    [columns, rowSelection, isMobile, hasExpandableRows],
  );

  return (
    <Table
      {...tableProps}
      rowKey="__channelRowKey"
      columns={sharedColumns}
      dataSource={rows}
      rowClassName={(record) =>
        record.__channelRowType
          ? `order-channel-row order-channel-${record.__channelRowType}`
          : ""
      }
      rowSelection={
        rowSelection
          ? {
              ...rowSelection,
              columnWidth: hasExpandableRows
                ? Math.max(Number(rowSelection.columnWidth) || 0, 64)
                : rowSelection.columnWidth,
              columnTitle: hasExpandableRows
                ? (node) => (
                    <span className="order-channel-selection-title">
                      {typeof rowSelection.columnTitle === "function"
                        ? rowSelection.columnTitle(node)
                        : rowSelection.columnTitle ?? node}
                    </span>
                  )
                : rowSelection.columnTitle,
              fixed: isMobile ? false : rowSelection.fixed,
              checkStrictly: true,
              getCheckboxProps: (record) =>
                record.__channelRowType
                  ? { disabled: true }
                  : (rowSelection.getCheckboxProps?.(record) ?? {}),
              renderCell: (checked, record, index, node) =>
                record.__channelRowType
                  ? null
                  : rowSelection.renderCell
                    ? rowSelection.renderCell(checked, record, index, node)
                    : node,
            }
          : undefined
      }
      expandable={{
        childrenColumnName: "__channelChildren",
        expandedRowKeys: expandedOrderKeys,
        onExpandedRowsChange: (keys) =>
          onExpandedOrderKeysChange(Array.from(keys)),
        expandIconColumnIndex: 0,
        indentSize: 0,
        expandIcon: ({ expanded, expandable, record, onExpand }) =>
          expandable ? (
            <button
              type="button"
              className={`order-channel-expand-button${rowSelection ? " order-channel-selection-arrow" : ""}`}
              aria-expanded={expanded}
              aria-label={t(
                expanded
                  ? "campaignDetail.creatorList.collapseChannels"
                  : "campaignDetail.creatorList.expandChannels",
              )}
              onClick={(event) => {
                event.stopPropagation();
                onExpand(record, event);
              }}
            >
              <DownArrowIcon
                className={expanded ? "" : "order-channel-arrow-collapsed"}
              />
            </button>
          ) : rowSelection && hasExpandableRows && !record.__channelRowType ? (
            <span
              aria-hidden="true"
              className="order-channel-selection-spacer"
            />
          ) : null,
      }}
    />
  );
}
